
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Ticket, User } from "@/types";
import { CheckCircle, UserCheck, UserX, AlertTriangle, Clock, Ban, Shield } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { format } from "date-fns";

// Mock user for simulation
const MOCK_USER: User = {
  id: "u1",
  name: "John Doe",
  email: "john@example.com",
  verificationStatus: "verified",
  verificationDate: new Date().toISOString(),
  verificationExpiry: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(), // 1 year from now
  documentsVerified: {
    idCard: true,
    selfie: true,
    addressProof: true
  },
  riskScore: 0,
  photoUrl: "/lovable-uploads/5d3d457b-1f96-443e-b101-9b10692fb838.png"
};

interface VerificationStatusProps {
  ticket: Ticket | null;
  onConfirm: (ticketId: string) => void;
}

export const VerificationStatus = ({ ticket, onConfirm }: VerificationStatusProps) => {
  const [isConfirmed, setIsConfirmed] = useState(false);
  const { toast } = useToast();

  if (!ticket) {
    return (
      <Card className="p-6 glass-card text-center text-muted-foreground">
        <p>Scan a ticket to see verification status</p>
      </Card>
    );
  }

  const status = MOCK_USER.verificationStatus;

  const getStatusIcon = () => {
    switch (status) {
      case "verified":
        return <CheckCircle className="w-6 h-6 text-green-500" />;
      case "pending":
        return <Clock className="w-6 h-6 text-yellow-500" />;
      case "failed":
        return <UserX className="w-6 h-6 text-red-500" />;
      case "expired":
        return <AlertTriangle className="w-6 h-6 text-orange-500" />;
      case "blacklisted":
        return <Ban className="w-6 h-6 text-red-700" />;
      default:
        return <Shield className="w-6 h-6 text-gray-500" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case "verified":
        return "text-green-700 bg-green-50";
      case "pending":
        return "text-yellow-700 bg-yellow-50";
      case "failed":
        return "text-red-700 bg-red-50";
      case "expired":
        return "text-orange-700 bg-orange-50";
      case "blacklisted":
        return "text-red-700 bg-red-50";
      default:
        return "text-gray-700 bg-gray-50";
    }
  };

  const handleConfirm = () => {
    setIsConfirmed(true);
    onConfirm(ticket.id);
    toast({
      title: "Entry Confirmed",
      description: "The guest has been permitted entry.",
    });
  };

  return (
    <Card className="p-6 glass-card animate-in">
      <div className="space-y-6">
        <div className="flex items-start justify-between">
          <div>
            <h3 className="text-xl font-semibold">Verification Status</h3>
            <p className="text-sm text-muted-foreground">ID checked via Scantek</p>
          </div>
          <div className={`flex items-center gap-2 px-3 py-1 rounded-full ${getStatusColor()}`}>
            {getStatusIcon()}
            <span className="text-sm font-medium capitalize">{status}</span>
          </div>
        </div>
        
        <div className="relative aspect-[3/4] w-full max-w-sm mx-auto rounded-lg overflow-hidden bg-gray-100">
          {MOCK_USER.photoUrl && (
            <img 
              src={MOCK_USER.photoUrl} 
              alt="ID Photo"
              className="w-full h-full object-cover"
            />
          )}
        </div>

        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-muted-foreground">Name</span>
            <span className="font-medium">{MOCK_USER.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Ticket ID</span>
            <span className="font-medium">{ticket.id}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Verified On</span>
            <span className="font-medium">
              {MOCK_USER.verificationDate && format(new Date(MOCK_USER.verificationDate), 'MMM dd, yyyy')}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-muted-foreground">Valid Until</span>
            <span className="font-medium">
              {MOCK_USER.verificationExpiry && format(new Date(MOCK_USER.verificationExpiry), 'MMM dd, yyyy')}
            </span>
          </div>
          
          <div className="border-t pt-3">
            <h4 className="font-medium mb-2">Documents Verified</h4>
            <div className="grid grid-cols-2 gap-2">
              {MOCK_USER.documentsVerified?.idCard && (
                <div className="flex items-center gap-2 text-sm text-green-700">
                  <CheckCircle className="w-4 h-4" />
                  <span>ID Card</span>
                </div>
              )}
              {MOCK_USER.documentsVerified?.selfie && (
                <div className="flex items-center gap-2 text-sm text-green-700">
                  <CheckCircle className="w-4 h-4" />
                  <span>Selfie</span>
                </div>
              )}
              {MOCK_USER.documentsVerified?.addressProof && (
                <div className="flex items-center gap-2 text-sm text-green-700">
                  <CheckCircle className="w-4 h-4" />
                  <span>Address</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <Button 
          className="w-full"
          onClick={handleConfirm}
          disabled={isConfirmed || status !== "verified"}
        >
          {isConfirmed ? "Entry Confirmed" : "Confirm Entry"}
        </Button>
      </div>
    </Card>
  );
};
